import { Injectable } from '@angular/core';
import { environment } from '@environments';
import { HandleError, HttpErrorHandler } from '../http-error-handler.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Rancho } from '@shared/models/ranchos/Rancho';
import { catchError, map, tap } from 'rxjs/operators';
import { UrlApi } from '@shared/enums/urlApi';
import { RanchoV2 } from '@shared/models/ranchos/RanchoV2';
import { RemitosRanchos } from '@shared/models/ranchos/RemitosRanchos';
import { ConsumoRancho } from '@shared/models/ranchos/ConsumoRancho';
import { Usuario } from '@shared/models/ranchos/Usuario';
import { Aerovale } from '@shared/models/despacho/Aerovale';
import { Observacion } from '@shared/models/despacho/Observacion';
import { RemitoFox } from '@shared/models/despacho/RemitoFox';
import { UsuarioService } from '@account/usuario.service';
import { AerovaleDetalle } from '@shared/models/despacho/AerovaleDetalle';
import { CuentaCliente } from '@shared/models/despacho/CuentaCliente';
import { SharedService } from '@shared/shared.service';
import { isNullOrUndefined } from '@shared/compat-tools';

@Injectable({
  providedIn: 'root'
})
export class RanchoService {

  baseUrl = environment.apiServer.ranchosBaseUrl;
  despachoUrl = environment.apiServer.baseUrl;
  clienteUrl = environment.apiServer.clientesBaseUrl;
  private clienteURL = this.clienteUrl + '/ClientesV1';
  private aerovalesV2Url = this.despachoUrl + '/AerovalesV2/';
  private ranchosUrl = this.baseUrl + UrlApi.ranchos + '/';
  private ranchosV2Url = this.baseUrl + UrlApi.ranchosV2;
  private consumosRanchosUrl = this.baseUrl + UrlApi.consumos + '/';
  private facturacionUrl = environment.apiServer.facturacionBaseUrl + "/ComprobantesV1";
  private observacionesUrl = this.despachoUrl + '/ObservacionesV1';
  private remitosURL = this.despachoUrl + '/RemitosV1/';
  private handleError: HandleError;

  private capturarAerovaleAnulado$: Subject<boolean> = new Subject;
  private archivoAerovaleAnulado$: Subject<File> = new Subject;

  private lastUpdated: Date;

  clientes: CuentaCliente[];

  constructor(
    private httpClient: HttpClient,
    httpErrorHandler: HttpErrorHandler,
    private usuarioService: UsuarioService,
    private sharedService: SharedService) {
    this.handleError = httpErrorHandler.createHandleError("Ranchos")
  }

  getAerovalesPorRangoEnAeroplantas(fechaInicio: string, fechaFin: string): Observable<Aerovale[]> {
    return this.httpClient.get<Aerovale[]>(
      this.aerovalesV2Url+"Aeroplanta/"+ "SCL", {
      params: {
        fechaDesde: fechaInicio,
        fechaHasta: fechaFin,
        sincronizados: 'true',
      }
    })
      .pipe(
        tap(() => {
          () => console.log(`obteniendo aerovales por rango en aeroplantas`)
        },
          catchError(this.handleError('Obtener Aerovales', new Aerovale()))
        ));
  }

  getAerovalePorIdyTipo(id: string): Observable<AerovaleDetalle> {
    return this.httpClient.get<AerovaleDetalle>(this.aerovalesV2Url + id)
      .pipe(
        catchError(this.handleError('Obtener aerovale', new AerovaleDetalle()))
      );
  }

  getMotivosAnulacion(obs: number): Observable<Observacion[]> {
    return this.httpClient.get<Observacion[]>(this.observacionesUrl + '?tipoObservacion=' + obs)
      .pipe(
        catchError(this.handleError('Obtener Observaciones', []))
      );
  }

  anularAerovale(aerovaleId: string, comentarios: string, observacionId: string, usuario: Usuario): Observable<boolean> {
    const headers = new HttpHeaders().set('usuarioDto', JSON.stringify(usuario));

    return this.httpClient.delete<boolean>(this.aerovalesV2Url + '?aerovaleId=' + aerovaleId + '&comentario=' + comentarios + '&observacion=' + observacionId,  { headers: headers})
  }

  modificarAerovale(aerovaleId: string, ctA_SGC: string): Observable<any> {

    return this.httpClient.put(this.facturacionUrl + '/ModificarCuentaRemito' + '?aerovaleId=' + aerovaleId + '&cta_sgc=' + ctA_SGC, null, {responseType: 'text'});
  }

  getClientesActivos(): Observable<CuentaCliente[]> {
    return this.httpClient.get<CuentaCliente[]>(this.clienteURL, {
      params: {
        lastUpdated: this.lastUpdated ? this.sharedService.dateToFormattedString(this.lastUpdated) : ''
      }
    })
      .pipe(
        map(nuevos => {
          if (this.lastUpdated) {
            if (nuevos) {
              const ctasNuevos = nuevos.map(c => c.ctA_SGC);
              const filtered = this.clientes.filter(c => isNullOrUndefined(ctasNuevos.find(ctaNuevo => ctaNuevo == c.ctA_SGC)));
              this.clientes = filtered;
              this.clientes.push(...nuevos);
            }
          } else {
            this.clientes = nuevos;
          }

          this.lastUpdated = new Date();

          const aeroplantaSoloPad = this.usuarioService.getAeroplantaActual().soloPAD;

          return this.clientes.filter(c => c.activo && (aeroplantaSoloPad ? c.soloPAD : true));
        })
      );
  }


  exportarAerovalesPorRango(fechaInicio: Date, fechaFin: Date): Observable<any> {
    const url = `${this.baseUrl}/AerovalesV2/${this.usuarioService.getAeroplantaActual().codigoAeroplanta}/Exportar&fechaDesde=${fechaInicio.toISOString()}&fechaHasta=${fechaFin.toISOString()}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.httpClient.get(url, { headers: headers, responseType: 'blob' as 'json' })
      .pipe(
        tap(() => console.log(`Descargando excel`))
      );
  }

  exportarAerovalesPorId(aerovalesId: string[]): Observable<any>{
    const url = `${this.baseUrl}/AerovalesV2/Exportar`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.httpClient.post(url,aerovalesId,{ headers: headers, responseType: 'blob' as 'json' })
      .pipe(
        tap(() => console.log(`Descargando excel`))
      );
  }

  getRemitoPorAerovaleId(id: string): Observable<RemitoFox>
  {
    return this.httpClient.get<RemitoFox>(this.remitosURL + id)
  }

  obtenerTodasLasObservaciones(): Observable<Observacion[]> {
    return this.httpClient.get<Observacion[]>(this.observacionesUrl)
      .pipe(
        catchError(this.handleError('Obtener observaciones', []))
      );
  }

  getRancho(codigoAeroplanta: string, codigoPais: string, codigoProducto: string, cuentaSGC: string, fecha?: string): Observable<Rancho> {
    return this.httpClient.get<Rancho>(this.ranchosUrl + 'Rancho', {
      params: {
        CodigoAeroplanta: codigoAeroplanta,
        CodigoDestino: codigoPais,
        CTA_SGC: cuentaSGC,
        CodigoProducto: codigoProducto,
        fecha: fecha? fecha : ''
      }
    })
      .pipe(
        tap(a => console.log(a)),
        map(a => a == null ? null : Object.assign(new Rancho(), a)),
        catchError(this.handleError('Obtener rancho', null))
      );
  }

  getRanchoPorCUIT(codigoAeroplanta: string, codigoPais: string, codigoProducto: string, cuit: string, fecha?: string): Observable<Rancho> {
    return this.httpClient.get<Rancho>(this.ranchosUrl + 'RanchoPorCuit', {
      params: {
        CodigoAeroplanta: codigoAeroplanta,
        CodigoDestino: codigoPais,
        cuit: cuit,
        CodigoProducto: codigoProducto,
        fecha: fecha? fecha : ''
      }
    })
      .pipe(
        tap(a => console.log(a)),
        map(a => a == null ? null : Object.assign(new Rancho(), a)),
        catchError(this.handleError('Obtener rancho', null))
      );
  }

  getRanchos(fechaDesde: string, fechaHasta: string, codigoAeroplanta: string): Observable<Rancho[]> {
    return this.httpClient.get<Rancho[]>(this.ranchosUrl, {
      params: {
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
        codigoAeroplanta: codigoAeroplanta,
      }
    })
    .pipe(
      map(rta => rta.map(r => Object.assign(new Rancho(), r)),
        catchError(this.handleError('Obtener Ranchos', new Rancho()))
      ));
  }

  getCapturarAerovaleAnulado() {
    return this.capturarAerovaleAnulado$.asObservable();
  }

  getArchivoAerovaleAnulado() {
    return this.archivoAerovaleAnulado$.asObservable();
  }

  setArchivoAerovaleAnulado(file:File) {
    this.archivoAerovaleAnulado$.next(file);
  }

  getRanchosPendientes(page: number, pageSize:number, codigoAeroplanta: string): Observable<RanchoV2[]> {
    return this.httpClient.get<RanchoV2[]>(this.ranchosV2Url + "/RanchosPendientes?page=" + page.toString() + "&pageSize=" + pageSize.toString() + "&codigoAeroplanta=" + codigoAeroplanta);
  }

  getRanchosConsumidos(page: number, pageSize:number, codigoAeroplanta: string, fecha: string): Observable<RanchoV2[]> {
    return this.httpClient.get<RanchoV2[]>(this.ranchosV2Url + "/RanchosConsumidos?page=" + page.toString() + "&pageSize=" + pageSize.toString() + "&codigoAeroplanta=" + codigoAeroplanta + "&fecha=" + fecha);
  }

  getRanchoPorCodigo(codigoRancho: string) : Observable<RanchoV2> {
    return this.httpClient.get<RanchoV2>(this.ranchosV2Url + "/" + codigoRancho);
  }

  setCapturarAerovaleAnulado(condicion:boolean) {
    this.capturarAerovaleAnulado$.next(condicion);
  }

  putConsumosRanchosV2(listaIds: string[]): Observable<RemitosRanchos[]> {
    return this.httpClient.put<RemitosRanchos[]>(this.facturacionUrl +  "/RemitosParaRanchos", listaIds);
  }

  validarAFIP(ranchoId: string, codigoRancho:string, codigoAeroplanta:string, usuario: Usuario) : Observable<string> {
    return this.httpClient.put<string>(this.ranchosUrl + ranchoId + "/ValidarRanchoAFIP?codigoRancho=" + codigoRancho + "&codigoAeroplanta=" + codigoAeroplanta, usuario);
  }

  putAsignarNumeroRancho(ranchoId: string, codigoRancho:string, rancho: RanchoV2) : Observable<RanchoV2> {
    return this.httpClient.put<RanchoV2>(this.ranchosUrl + ranchoId + "/AsignarNumeroDeRancho?codigoRancho=" + codigoRancho,rancho)
  }

  eliminarRanchosInvidual(ranchoId:string) {
    return this.httpClient.delete(this.ranchosUrl + ranchoId + "/EliminarRancho", {responseType: 'text'})
  }

  putDesactivarRanchos(ranchoId:string, usuario: Usuario) {
    return this.httpClient.put(this.ranchosUrl + ranchoId + "/DesactivarRancho", usuario, {responseType: 'text'})
  }

  eliminarRanchosMasivos(numeroLote:string) {
    return this.httpClient.delete(this.ranchosUrl + numeroLote + "/EliminarLoteRancho", {responseType: 'text'});
  }

  putActualizarEstadoConsumosRanchosV2(aerovaleId:string, estado:number, usuario: Usuario) : Observable<ConsumoRancho> {
    return this.httpClient.put<ConsumoRancho>(this.consumosRanchosUrl + aerovaleId + "/ActualizarEstadoConsumoRancho?estadoConsumo=" + estado, usuario);
  }

}
